import getFullComposeViewState from '../selectors/getFullComposeViewState';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import type { LocationMetricInfo } from 'owa-calendar-helpers-types/lib/LocationMetricTypes';
import { mutator, mutatorAction } from 'satcheljs';
import {
    increaseTimeSpentInRoomBookingPanel,
    logComposeFormOpenedTimes,
    logSchedulingGridUsed,
    logQuickComposeControlUsed,
    logTimeSuggestionStartLastSet,
    initializeTimeSuggestionMetricsInfo,
    logTimeSuggestionShown,
    logTimeSuggestionClickedIndex,
    logTimeSuggestionNotFoundCount,
    logTimeSuggestionSAClickedCount,
    logTimeSuggestionInteraction,
    logFindMeetingTimesRequestId,
    logChangeTimeActions,
    clearTimeSuggestionStartLastSet,
    logTimeSuggestionStartFirstSet,
    clearTimeSuggestionStartFirstSet,
    logQuickComposeToFullComposeEntryType,
    logQuickComposePivotType,
    logIsBusyResourceSendClicked,
    logDidExpandEventBodyEditor,
} from 'owa-calendar-compose-form-actions';
import {
    createLocationMetricsFromEnhancedLocation,
    getLocationMetricId,
} from 'owa-calendar-forms-utils/lib/utils/createLocationMetrics';
import type { AutoRoomBookingMetricInfo } from 'owa-calendar-helpers-types/lib/AutoRoomBookingMetricInfo';
import { timestamp } from 'owa-datetime';

const QC_CONTROL_USAGE_DEFAULT = 0;

export const logComposeFormOpenedTimesMutator = mutator(logComposeFormOpenedTimes, () => {
    const newTransitionTimestamp = timestamp();

    let store = getComposeStore();
    let { openedForm, metricsInfo } = store;
    if (openedForm && metricsInfo) {
        const timeSinceLastTransition = metricsInfo.formTransitionTimestamp
            ? newTransitionTimestamp - metricsInfo.formTransitionTimestamp
            : 0;

        switch (openedForm) {
            case 'QuickCompose':
                metricsInfo.timeInQuickCompose = timeSinceLastTransition;
                break;
            case 'FullCompose':
                let fullComposeViewState = getFullComposeViewState();
                if (fullComposeViewState?.showSchedulingAssistant) {
                    metricsInfo.timeInSchedulingAssistant =
                        (metricsInfo.timeInSchedulingAssistant || 0) + timeSinceLastTransition;
                } else {
                    metricsInfo.timeInFullCompose =
                        (metricsInfo.timeInFullCompose || 0) + timeSinceLastTransition;
                }
                break;
        }
    }
    store.metricsInfo = { ...metricsInfo, formTransitionTimestamp: newTransitionTimestamp };
});

export const logSchedulingGridUsedMutator = mutator(logSchedulingGridUsed, actionMessage => {
    let store = getComposeStore();
    let { metricsInfo } = store;
    if (metricsInfo) {
        metricsInfo.schedulingGridUsed = true;
    }
});

export const increaseTimeSpentInRoomBookingPanelMutator = mutator(
    increaseTimeSpentInRoomBookingPanel,
    actionMessage => {
        let timeInRoomBookingPanelSession = actionMessage.timeInRoomBookingPanelSession;
        let store = getComposeStore();

        if (store.metricsInfo) {
            store.metricsInfo.timeInRoomBookingPanel =
                (store.metricsInfo.timeInRoomBookingPanel || 0) + timeInRoomBookingPanelSession;
        }
    }
);

export const logQuickComposeControlUsedMutator = mutator(
    logQuickComposeControlUsed,
    actionMessage => {
        let store = getComposeStore();
        let { metricsInfo } = store;
        if (!metricsInfo || !metricsInfo.quickComposeControlUsageMetrics) {
            return;
        }

        // Get the dicts and their current values defaulting to 0;
        const {
            usageDict,
            startTimeDict,
            durationDict,
        } = metricsInfo.quickComposeControlUsageMetrics;
        const { control, isFocus } = actionMessage;
        const currentUsageCount = usageDict[control] || QC_CONTROL_USAGE_DEFAULT;
        const currentDuration = durationDict[control] || QC_CONTROL_USAGE_DEFAULT;
        const startTime = startTimeDict[control];

        const nowTimestamp = timestamp();

        if (isFocus) {
            // If the user is focusing a control, increment the usage, and save the start time for duration calculations.
            usageDict[control] = currentUsageCount + 1;
            startTimeDict[control] = nowTimestamp;
        } else if (startTime) {
            // Make sure we have a start time before calculating the duration.
            durationDict[control] = currentDuration + (nowTimestamp - startTime);
            startTimeDict[control] = null;
        }
    }
);

export const logTimeSuggestionStartLastSetMutator = mutator(
    logTimeSuggestionStartLastSet,
    actionMessage => {
        const metricsInfo = getComposeStore().metricsInfo;
        if (!metricsInfo) {
            // This mutator action is called from other packages. So, it is possible we call it when the metricsInfo is not initialized yet
            // For instance, the suggestions package calls this when we initialize Suggestions.
            return;
        }
        metricsInfo.timeSuggestionStartLastSet.push(actionMessage.suggestionStart);
    }
);

export const clearTimeSuggestionStartLastSetMutator = mutator(
    clearTimeSuggestionStartLastSet,
    () => {
        const metricsInfo = getComposeStore().metricsInfo;
        if (!metricsInfo) {
            // This mutator action is called from other packages. So, it is possible we call it when the metricsInfo is not initialized yet
            // For instance, the suggestions package calls this when we initialize Suggestions.
            return;
        }
        metricsInfo.timeSuggestionStartLastSet = [];
    }
);

export const logTimeSuggestionStartFirstSetMutator = mutator(logTimeSuggestionStartFirstSet, () => {
    const metricsInfo = getComposeStore().metricsInfo;
    if (!metricsInfo) {
        // This mutator action is called from other packages. So, it is possible we call it when the metricsInfo is not initialized yet
        // For instance, the suggestions package calls this when we initialize Suggestions.
        return;
    }
    metricsInfo.timeSuggestionStartFirstSet = metricsInfo.timeSuggestionStartLastSet;
});

export const clearTimeSuggestionStartFirstSetMutator = mutator(
    clearTimeSuggestionStartFirstSet,
    () => {
        const metricsInfo = getComposeStore().metricsInfo;
        if (!metricsInfo) {
            // This mutator action is called from other packages. So, it is possible we call it when the metricsInfo is not initialized yet
            // For instance, the suggestions package calls this when we initialize Suggestions.
            return;
        }
        metricsInfo.timeSuggestionStartFirstSet = [];
    }
);

export const initializeTimeSuggestionMetricsInfoMutator = mutator(
    initializeTimeSuggestionMetricsInfo,
    () => {
        const metricsInfo = getComposeStore().metricsInfo;
        if (!metricsInfo) {
            // This mutator action is called from other packages. So, it is possible we call it when the metricsInfo is not initialized yet
            // For instance, the suggestions package calls this when we initialize Suggestions.
            return;
        }

        metricsInfo.timeSuggestionShown = [];
        metricsInfo.timeSuggestionClickedIndex = [];
        metricsInfo.timeSuggestionNotFoundCount = 0;
        metricsInfo.timeSuggestionSAClickedCount = 0;
        metricsInfo.timeSuggestionStartLastSet = [];
        metricsInfo.timeSuggestionStartFirstSet = [];
        metricsInfo.timeSuggestionInteraction = false;
        metricsInfo.findMeetingTimesRequestId = '';
    }
);

export const logTimeSuggestionShownMutator = mutator(logTimeSuggestionShown, actionMessage => {
    const metricsInfo = getComposeStore().metricsInfo;
    if (!metricsInfo) {
        // This mutator action is called from other packages. So, it is possible we call it when the metricsInfo is not initialized yet
        // For instance, the suggestions package calls this when we initialize Suggestions.
        return;
    }

    metricsInfo.timeSuggestionShown.push(actionMessage.suggestionsCount);
});

export const logFindMeetingTimesRequestIdMutator = mutator(
    logFindMeetingTimesRequestId,
    actionMessage => {
        const metricsInfo = getComposeStore().metricsInfo;
        if (!metricsInfo) {
            // This mutator action is called from other packages. So, it is possible we call it when the metricsInfo is not initialized yet
            // For instance, the suggestions package calls this when we initialize Suggestions.
            return;
        }

        metricsInfo.findMeetingTimesRequestId = actionMessage.findMeetingTimesRequestId;
    }
);

export const logTimeSuggestionClickedIndexMutator = mutator(
    logTimeSuggestionClickedIndex,
    actionMessage => {
        const metricsInfo = getComposeStore().metricsInfo;
        if (!metricsInfo) {
            // This mutator action is called from other packages. So, it is possible we call it when the metricsInfo is not initialized yet
            // For instance, the suggestions package calls this when we initialize Suggestions.
            return;
        }

        metricsInfo.timeSuggestionInteraction = true;
        metricsInfo.timeSuggestionClickedIndex.push(actionMessage.suggestionIndex);
    }
);

export const logTimeSuggestionNotFoundCountMutator = mutator(logTimeSuggestionNotFoundCount, () => {
    const metricsInfo = getComposeStore().metricsInfo;
    if (!metricsInfo) {
        // This mutator action is called from other packages. So, it is possible we call it when the metricsInfo is not initialized yet
        // For instance, the suggestions package calls this when we initialize Suggestions.
        return;
    }

    metricsInfo.timeSuggestionNotFoundCount++;
});

export const logTimeSuggestionSAClickedCountMutator = mutator(
    logTimeSuggestionSAClickedCount,
    () => {
        const metricsInfo = getComposeStore().metricsInfo;
        if (!metricsInfo) {
            // This mutator action is called from other packages. So, it is possible we call it when the metricsInfo is not initialized yet
            // For instance, the suggestions package calls this when we initialize Suggestions.
            return;
        }

        metricsInfo.timeSuggestionSAClickedCount++;
    }
);

export const logTimeSuggestionInteractionMutator = mutator(logTimeSuggestionInteraction, () => {
    const metricsInfo = getComposeStore().metricsInfo;
    if (!metricsInfo) {
        return;
    }

    metricsInfo.timeSuggestionInteraction = true;
});

export const logChangeTimeSourceActionsMutator = mutator(logChangeTimeActions, actionMessage => {
    const metricsInfo = getComposeStore().metricsInfo;
    if (!metricsInfo) {
        // This mutator action is called from other packages. So, it is possible we call it when the metricsInfo is not initialized yet
        // For instance, the suggestions package calls this when we initialize Suggestions.
        return;
    }

    if (!metricsInfo.changeTimeActions) {
        metricsInfo.changeTimeActions = [];
    }

    if (actionMessage.sourceAction) {
        metricsInfo.changeTimeActions.push(actionMessage.sourceAction);
    }
});

export let initializeLocationMetrics = mutatorAction('initializeLocationMetrics', () => {
    const { metricsInfo, event } = getComposeStore();

    if (metricsInfo) {
        metricsInfo.locationMetrics = {};
        event.Locations.forEach(location => {
            const locationId = getLocationMetricId(location.Id, location.PostalAddress);
            metricsInfo.locationMetrics[locationId] = createLocationMetricsFromEnhancedLocation(
                location,
                'ExistingLocation'
            );
        });
    }
});

export const addLocationMetrics = mutatorAction(
    'addLocationMetrics',
    (newLocationMetrics: { [index: string]: LocationMetricInfo }) => {
        const metricsInfo = getComposeStore().metricsInfo;
        if (metricsInfo) {
            Object.keys(newLocationMetrics).forEach(newLocationId => {
                if (!metricsInfo.locationMetrics[newLocationId]) {
                    metricsInfo.locationMetrics[newLocationId] = newLocationMetrics[newLocationId];
                }
            });
        }
    }
);

export const removeLocationMetrics = mutatorAction(
    'removeLocationMetrics',
    (removedLocationMetrics: string[]) => {
        const metricsInfo = getComposeStore().metricsInfo;
        if (metricsInfo) {
            removedLocationMetrics.forEach(
                locationId => delete metricsInfo.locationMetrics[locationId]
            );
        }
    }
);

export const updateAutoRoomBookingMetric = mutatorAction(
    'updateAutoRoomBookingMetric',
    (metric: Partial<AutoRoomBookingMetricInfo>) => {
        const metricsInfo = getComposeStore().metricsInfo;
        if (metricsInfo) {
            metricsInfo.autoRoomBookingMetric = {
                ...metricsInfo.autoRoomBookingMetric,
                ...metric,
            };
        }
    }
);

mutator(logQuickComposeToFullComposeEntryType, actionMessage => {
    const metricsInfo = getComposeStore().metricsInfo;
    if (metricsInfo) {
        metricsInfo.quickComposeToFullComposeEntry = actionMessage.entryType;
    }
});

mutator(logQuickComposePivotType, actionMessage => {
    const metricsInfo = getComposeStore().metricsInfo;
    if (metricsInfo) {
        metricsInfo.quickComposePivotType = actionMessage.pivot;
    }
});

mutator(logIsBusyResourceSendClicked, actionMessage => {
    const metricsInfo = getComposeStore().metricsInfo;
    if (metricsInfo) {
        metricsInfo.isBusyResourceSendClicked = actionMessage.send;
    }
});

export const initializeTimePickerMetricsInfo = mutatorAction(
    'initializeTimePickerMetricsInfo',
    () => {
        const metricsInfo = getComposeStore().metricsInfo;

        if (metricsInfo) {
            metricsInfo.startTimeChangedCount = 0;
            metricsInfo.endTimeChangedCount = 0;
            metricsInfo.startTimeManuallyChangedCount = 0;
            metricsInfo.endTimeManuallyChangedCount = 0;
        }
    }
);

export const logStartTimeChangedCount = mutatorAction('logStartTimeChangedCount', () => {
    const metricsInfo = getComposeStore().metricsInfo;

    if (metricsInfo) {
        metricsInfo.startTimeChangedCount++;
    }
});

export const logEndTimeChangedCount = mutatorAction('logEndTimeChangedCount', () => {
    const metricsInfo = getComposeStore().metricsInfo;

    if (metricsInfo) {
        metricsInfo.endTimeChangedCount++;
    }
});

export const logStartTimeManuallyChangedCount = mutatorAction(
    'logStartTimeManuallyChangedCount',
    () => {
        const metricsInfo = getComposeStore().metricsInfo;

        if (metricsInfo) {
            metricsInfo.startTimeManuallyChangedCount++;
        }
    }
);

export const logEndTimeManuallyChangedCount = mutatorAction(
    'logEndTimeManuallyChangedCount',
    () => {
        const metricsInfo = getComposeStore().metricsInfo;

        if (metricsInfo) {
            metricsInfo.endTimeManuallyChangedCount++;
        }
    }
);

mutator(logDidExpandEventBodyEditor, () => {
    const metricsInfo = getComposeStore().metricsInfo;

    if (metricsInfo) {
        metricsInfo.didExpandEventBodyEditor = true;
    }
});
